/**
 * Per-locale synonym dictionary used at build time to expand each doc's
 * `bodyTokens`. Each `SynonymGroup` is a bidirectional cluster — if any
 * term in the group appears in a doc's tokens, every other term in the
 * group is appended before normalization, so a query for any one
 * vocabulary picks up docs written in another.
 *
 * This is the seed list. Extend it from the PostHog `search_query` events,
 * filtered to zero-result queries — that's the data telling you exactly
 * which words don't currently match anything.
 *
 * ## Curation rules
 *
 * - **Single tokens only.** `expandSynonyms` matches via the tokenizer's
 *   word boundaries, so multi-word phrases like "blood sugar" don't fit.
 *   Pick the discriminating word ("glucose") instead.
 * - **Lowercase, no punctuation.** Tokens are matched against
 *   `tokenize()` output which is already lowercased and ASCII-folded.
 * - **Bidirectional groups.** Don't worry about which word is "primary".
 *   `["mri", "mrt"]` makes both directions work.
 * - **Tight clusters beat broad clusters.** A false-positive synonym hurts
 *   relevance more than a missing synonym hurts recall — you can always
 *   feel a wrong result, but a missing result that the query data flags
 *   gets quietly added later.
 * - **Spelling variants count.** UK/US (`anaemia`/`anemia`) and Latin
 *   variants (`haemoglobin`/`hemoglobin`) belong here too.
 */

import { tokenize } from "./engine"

export type SynonymGroup = string[]

/** Cross-locale clusters — same term lives in multiple languages or is
 *  language-neutral (acronyms, brand names). Imported into every locale. */
const COMMON: SynonymGroup[] = [
  // Imaging — identical scan, different acronym per language.
  ["mri", "mrt", "irm", "kernspin", "scan", "imaging"],
  // Bone density.
  ["dexa", "dxa"],
  // Computed tomography.
  ["ct", "cat", "computed", "tomography"],
  // Cardiac biomarkers + acronyms that travel verbatim.
  ["hba1c", "a1c"],
  ["ldl", "hdl", "apob", "lipoprotein"],
  ["tsh", "t3", "t4", "ft3", "ft4"],
  ["crp", "hscrp"],
  ["psa"],
  ["alt", "sgpt", "alat"],
  ["ast", "sgot", "asat"],
  ["ggt"],
  // Vitamins.
  ["b12", "cobalamin"],
  ["b9", "folate", "folic"],
  ["d3", "cholecalciferol"],
  // Trace elements + heavy metals — same element, different vocabulary
  // across DE / EN / FR / NL plus the chemical symbol. Driven by PostHog
  // zero-result data ("zink"/"zinc", "bleiwerte"/"blei"/"lead", etc.).
  ["zink", "zinc", "zn"],
  ["kupfer", "koper", "copper", "cu"],
  ["selen", "seleen", "selenium", "se"],
  ["blei", "bleiwerte", "lood", "lead", "pb"],
  ["quecksilber", "kwik", "mercury", "hg"],
  ["arsen", "arsenic"],
  ["kadmium", "cadmium"],
  ["magnesium", "mg"],
  ["kalzium", "calcium"], // 'ca' deliberately omitted — collides with
  // cancer abbreviations (CA-125, CA 19-9) and is too short for fuzzy
  ["natrium", "sodium"],
  ["jod", "jodium", "iodine"],
  // Sex / adrenal hormones — patient-side abbreviations.
  ["dht", "dihydrotestosterone"],
  ["estradiol", "e2"],
  ["dheas", "dhea"],
  // Cardiac, hepatic, renal, glycemic, inflammation — common acronyms
  // that travel verbatim across locales.
  ["bnp", "ntprobnp"],
  ["alp", "alkaline", "phosphatase"],
  ["bun", "urea"],
  ["egfr", "gfr", "creatinine"],
  ["ck", "creatine", "kinase"],
  ["ldh", "lactate", "dehydrogenase"],
  // Cross-spelling families bridging query-side variants to EXISTING catalog
  // content (verified present 2026-06-10). German c/k and oe/ö spellings from
  // the PostHog zero-result data. Only added where a real doc exists to find:
  // e.g. "kortisol" reaching the adrenal-insufficiency disease + FAQ that
  // "cortisol" already surfaces. Biomarkers genuinely absent from the catalog
  // (homocysteine, IGF-1, cystatin C, zinc) are a content gap, not a synonym
  // one — adding a synonym for them would find nothing.
  [
    "cortisol",
    "kortisol",
    "cortisone",
    "kortison",
    "hydrocortisone",
    "hydrokortison",
  ],
  ["estrogen", "oestrogen", "östrogen", "oestrogeen", "estradiol"],
]

/** English (and en-NL fallback). Includes UK/US spelling variants. */
const EN: SynonymGroup[] = [
  ["blood", "bloodwork", "bloodtest", "panel", "labs", "lab"],
  ["test", "panel", "screening"],
  ["checkup", "exam", "screening", "preventive", "preventative"],
  ["doctor", "physician", "gp", "clinician"],
  ["price", "pricing", "cost", "fee", "fees"],
  ["insurance", "coverage", "reimbursement", "insurer"],
  // British / American spellings.
  ["anaemia", "anemia"],
  ["haemoglobin", "hemoglobin"],
  ["tumour", "tumor"],
  ["oestrogen", "estrogen", "estradiol"],
  ["foetal", "fetal"],
  ["paediatric", "pediatric"],
  // Conditions / risk areas — common natural-language → clinical-term jumps.
  ["heart", "cardiac", "cardiovascular", "coronary"],
  ["liver", "hepatic", "fatty"],
  ["kidney", "renal"],
  ["brain", "neurological", "neuro", "cerebral", "dementia", "alzheimer"],
  ["thyroid"],
  ["diabetes", "diabetic", "glucose", "sugar"],
  ["cholesterol", "lipids", "lipid"],
  ["bone", "osteoporosis", "density"],
  ["breast", "mamma", "mammogram", "mammography"],
  ["prostate"],
  ["pelvis", "pelvic"],
  // Generic word only — do NOT add specific markers (testosterone, estrogen)
  // here. As bidirectional synonyms they make a search for one specific
  // hormone expand to "hormone" and match every hormone in the catalog, so the
  // wrong one wins by alphabetical order (searching "testosterone" surfaced
  // "Luteinizing Hormone"). Specific markers keep their own groups below.
  ["hormone", "hormones"],
  ["iron", "ferritin"],
  ["vitamin", "vitamins"],
  ["fatigue", "tired", "tiredness", "exhaustion"],
  // Patient-side abbreviations and clinical aliases — driven by the
  // zero-result data ("cortsol"→cortisol, "homoc"→homocysteine,
  // "trt"→testosterone, "ptau"→tau). The catalog NAMES are localized
  // (de "Testosteron, gesamt", fr "Testostérone libre"), so a plain
  // "testosteron"/"testo" already matches by name — this group only adds
  // the abbreviations the names don't contain ("trt").
  ["testosterone", "testo", "trt"],
  ["cortisol", "hydrocortisone"],
  ["homocysteine", "hcy"],
  ["hemoglobin", "haemoglobin", "hgb", "hb"],
  ["bilirubin", "tbil"],
  ["prostate", "psa"],
  ["thyroxine", "tsh"],
  // Anatomy / scope words. Hyphenated forms like "full-body" tokenize to
  // ["full", "body"] so they never match as a single token — we keep the
  // single-token forms and rely on tokenized membership for the rest.
  ["fullbody", "wholebody", "totalbody"],
]

/** German (de). */
const DE: SynonymGroup[] = [
  [
    "blut",
    "bluttest",
    "blutuntersuchung",
    "blutbild",
    "blutprobe",
    "labor",
    "laboruntersuchung",
  ],
  ["test", "panel", "untersuchung", "screening"],
  ["checkup", "vorsorge", "vorsorgeuntersuchung", "präventiv", "untersuchung"],
  ["arzt", "ärztin", "doktor", "mediziner", "hausarzt"],
  ["preis", "preise", "kosten", "tarif", "tarife", "gebühr"],
  ["versicherung", "krankenkasse", "kostenübernahme", "krankenversicherung"],
  ["krebs", "tumor", "karzinom", "geschwulst"],
  ["herz", "kardio", "kardiologisch", "kreislauf", "koronar"],
  ["leber", "hepatisch", "fettleber"],
  ["niere", "nieren", "renal"],
  ["gehirn", "hirn", "neurologisch", "demenz", "alzheimer"],
  ["schilddrüse"],
  ["diabetes", "zuckerkrankheit", "blutzucker", "glukose", "zucker"],
  ["cholesterin", "lipide", "lipid"],
  ["knochen", "knochendichte", "osteoporose"],
  ["brust", "mamma", "mammographie"],
  ["prostata"],
  ["becken", "pelvis"],
  // Generic word only (see EN note) — specific markers kept out so a
  // "Testosteron" search doesn't expand to every hormone and surface
  // "Luteinisierendes Hormon" instead.
  ["hormon", "hormone"],
  ["eisen", "ferritin"],
  ["vitamin", "vitamine"],
  ["müde", "müdigkeit", "erschöpfung"],
  ["ganzkörper", "vollkörper", "ganzkörperscan"],
  // Single-token DE entries that hook into the cross-locale COMMON
  // groups above. Including them here is a no-op for German-locale
  // search (already inherited via [...COMMON, ...DE]) but documents
  // intent — these are the German words the dictionary covers.
  ["kortisol", "hydrokortison"],
  ["homocystein"],
  ["östrogen", "estradiol"],
  ["nüchtern", "fasten"],
  ["migräne"],
]

/** French (fr). */
const FR: SynonymGroup[] = [
  ["sang", "sanguin", "sanguine", "laboratoire", "labo"],
  ["test", "bilan", "panel", "analyse", "analyses"],
  ["bilan", "checkup", "contrôle", "examen", "préventif"],
  ["médecin", "docteur", "généraliste", "praticien"],
  ["prix", "tarif", "tarifs", "coût", "frais"],
  ["assurance", "remboursement", "couverture", "assureur"],
  ["cancer", "tumeur", "carcinome"],
  ["cœur", "coeur", "cardiaque", "cardiovasculaire", "coronaire"],
  ["foie", "hépatique"],
  ["rein", "reins", "rénal"],
  ["cerveau", "cérébral", "neurologique", "démence", "alzheimer"],
  ["thyroïde"],
  ["diabète", "diabétique", "glycémie", "glucose", "sucre"],
  ["cholestérol", "lipides", "lipidique"],
  ["os", "densité", "ostéoporose"],
  ["sein", "mammaire", "mamma", "mammographie"],
  ["prostate"],
  ["bassin", "pelvis", "pelvien"],
  // Generic word only (see EN note).
  ["hormone", "hormones"],
  ["fer", "ferritine"],
  ["vitamine", "vitamines"],
  ["fatigue", "fatigué", "épuisement"],
  ["corps", "intégral", "entier"],
  // FR clinical aliases — mirror the EN clinical-name pairs.
  ["cortisol", "hydrocortisone"],
  ["homocystéine"],
  ["œstrogène", "oestrogene", "estradiol"],
]

/** Dutch (nl). */
const NL: SynonymGroup[] = [
  [
    "bloed",
    "bloedtest",
    "bloedonderzoek",
    "bloedanalyse",
    "bloedwaarden",
    "lab",
    "labo",
    "laboratorium",
  ],
  ["test", "onderzoek", "panel", "screening"],
  ["checkup", "controle", "keuring", "onderzoek", "preventie", "preventief"],
  ["arts", "huisarts", "dokter", "specialist"],
  ["prijs", "prijzen", "kosten", "tarief", "tarieven"],
  ["verzekering", "vergoeding", "zorgverzekeraar", "zorgverzekering"],
  ["kanker", "tumor", "carcinoom"],
  ["hart", "cardiologisch", "vaat", "vaatziekten", "coronair"],
  ["lever", "hepatisch", "leververvetting"],
  ["nier", "nieren", "renaal"],
  ["hersenen", "brein", "neurologisch", "dementie", "alzheimer"],
  ["schildklier"],
  ["diabetes", "suikerziekte", "bloedsuiker", "glucose", "suiker"],
  ["cholesterol", "lipiden", "vetten"],
  ["bot", "botten", "botdichtheid", "osteoporose"],
  ["borst", "mamma", "mammografie"],
  ["prostaat"],
  ["bekken", "pelvis"],
  // Generic word only (see EN note).
  ["hormoon", "hormonen"],
  ["ijzer", "ferritine"],
  ["vitamine", "vitamines"],
  ["moe", "moeheid", "vermoeidheid"],
  ["fullbody", "totaal"],
  // NL clinical aliases.
  ["cortisol", "hydrocortison"],
  ["homocysteine"],
  ["oestrogeen", "estradiol"],
]

export const SYNONYMS: Record<string, SynonymGroup[]> = {
  en: [...COMMON, ...EN],
  de: [...COMMON, ...DE],
  fr: [...COMMON, ...FR],
  nl: [...COMMON, ...NL],
  // en-NL inherits the EN list — same audience, just different geography.
  // Diverge it once we see country-specific zero-result queries.
  "en-NL": [...COMMON, ...EN],
}

/**
 * Append every synonym from any group whose terms appear in `text` to the
 * end of `text`. Idempotent — calling it twice produces the same expanded
 * string, modulo whitespace. Build-time only.
 *
 * Matching is via tokenized membership, NOT substring search, so "mri"
 * doesn't match "amri" or "mri-scan-prep" partial words. Multi-word
 * synonyms are unsupported by design (see header comment).
 */
export function expandSynonyms(
  text: string,
  groups: ReadonlyArray<SynonymGroup>
): string {
  if (!text || groups.length === 0) return text
  const tokenSet = new Set(tokenize(text))
  if (tokenSet.size === 0) return text
  const additions: string[] = []
  for (const group of groups) {
    for (const term of group) {
      if (tokenSet.has(term)) {
        additions.push(...group)
        break
      }
    }
  }
  return additions.length > 0 ? `${text} ${additions.join(" ")}` : text
}
